/**
 * A one-of-N choice as real buttons (user, 12/10/2026: "it must be very
 * logical to see which one is selected"): every option is an outlined
 * button with a radio circle; the picked one is filled in the brand colour
 * with a white dot. Used by Starts, Ends and A list fails.
 * For Media Devoted | Created By Ciro Autiero
 */
import { cn } from '@/lib/utils';

import { CHOICE_CHIP, CHOICE_OFF, CHOICE_ON } from './field-style';

export interface ChoiceOption<V extends string> {
  value: V;
  label: string;
}

interface Props<V extends string> {
  options: readonly ChoiceOption<V>[];
  value: V;
  onChange: (value: V) => void;
  ariaLabel: string;
}

export function ChoiceChips<V extends string>({ options, value, onChange, ariaLabel }: Props<V>) {
  return (
    <div role="radiogroup" aria-label={ariaLabel} className="flex flex-wrap items-center gap-2">
      {options.map((o) => {
        const active = o.value === value;
        return (
          <button
            key={o.value}
            type="button"
            role="radio"
            aria-checked={active}
            onClick={() => onChange(o.value)}
            className={cn(CHOICE_CHIP, 'gap-2 pl-2.5 pr-3.5', active ? CHOICE_ON : CHOICE_OFF)}
          >
            <span
              aria-hidden
              className={cn(
                'flex h-3.5 w-3.5 shrink-0 items-center justify-center rounded-full border-[1.5px] transition-colors duration-200',
                active ? 'border-primary-foreground' : 'border-muted-foreground/50',
              )}
            >
              <span className={cn('h-1.5 w-1.5 rounded-full bg-primary-foreground transition-transform duration-200 motion-reduce:transition-none', active ? 'scale-100' : 'scale-0')} />
            </span>
            {o.label}
          </button>
        );
      })}
    </div>
  );
}
